import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { IconButton, Link } from '@mui/material';
import Links from '../../../constants/links';
import { deletePayoutMethod } from '../../../api';
import { USER_PAYOUT_METHOD } from '../../../constants/queryData';
import StatusSnackbar from '../../common/Snackbar/StatusSnackbar';
import PayPalIcon from '../../icons/PayPalIcon';
import LogInWithPayPal from './LogInWithPayPal';
import { Button } from '../../common';

const PayPalEmail = ({ email, userPayoutMethodId }) => {
  const queryClient = useQueryClient();
  const [snackbar, setSnackbar] = useState({});
  const [isHovered, setIsHovered] = useState(false);

  const { mutate: disconnectPayPal, isLoading } = useMutation({
    mutationFn: () => deletePayoutMethod(userPayoutMethodId),
    onSuccess: () => {
      queryClient.setQueryData(USER_PAYOUT_METHOD, {});
      setSnackbar({
        message: 'Your email was successfully disconnected',
        status: 'success',
      });
    },
    onError: (error) => {
      const [apiError] = error?.response?.data?.errors || [];
      setSnackbar({
        message: apiError ? apiError.message : 'Something went wrong.',
        status: 'error',
      });
    },
  });

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({});
  };

  return (
    <>
      <StatusSnackbar status={snackbar.status} message={snackbar.message} onClose={handleClose} />
      <h3>You&apos;ve Earned Affiliate Credits</h3>
      <p>
        {email ? (
          <>
            You&apos;ll be paid out regularly to this PayPal account.{' '}
            <Link
              href={Links.REFERRAL_PAYOUTS_URL}
              target="_blank"
              color="brightBlue"
              underline="hover"
            >
              Learn more...
            </Link>
          </>
        ) : (
          <>
            Connect a PayPal account to receive payouts regularly.{' '}
            <Link
              href={Links.REFERRAL_PAYOUTS_URL}
              target="_blank"
              color="brightBlue"
              underline="hover"
            >
              Learn more...
            </Link>
          </>
        )}
      </p>
      {email ? (
        <Box
          display="flex"
          alignItems="center"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Button
            onClick={() => window.open(Links.PAYPAL_LOGIN_URL, '_blank')}
            disabled={isLoading}
            sx={{
              py: 1,
              px: 3,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'paypalGray',
              borderRadius: '12rem',
              width: 'fit-content',
              textTransform: 'none',
              color: 'black',
              '&:hover': {
                bgcolor: 'lightgray',
              },
            }}
          >
            <PayPalIcon />
            <Typography variant="body1" ml={1} sx={{ color: 'black' }}>
              {email}
            </Typography>
          </Button>
          <IconButton
            sx={{
              ml: 1.5,
              visibility: isHovered ? 'visible' : 'hidden',
              transition: 'visibility 0.1s',
            }}
            title="Disconnect PayPal email"
            disabled={isLoading}
            onClick={disconnectPayPal}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ) : (
        <LogInWithPayPal setSnackbar={setSnackbar} />
      )}
    </>
  );
};
PayPalEmail.propTypes = {
  email: PropTypes.string,
  userPayoutMethodId: PropTypes.number,
};
PayPalEmail.defaultProps = {
  email: null,
  userPayoutMethodId: null,
};
export default PayPalEmail;
